(() => {
  if (customElements.get('babauba-looks')) return;
  const stateSection = 'babauba-bonus-state';
  class BabaubaLooks extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.entry = this.querySelector('[data-look-entry]');
      this.builders = [...this.querySelectorAll('[data-look-builder]')];
      this.eligibleCount = Number(this.querySelector('[data-bonus-state]')?.dataset.count || 0);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'de-AT', { style: 'currency', currency: this.dataset.currency });
      this.rows = new Map();
      for (const builder of this.builders) {
        const rows = [...builder.querySelectorAll('[data-look-item]')];
        this.rows.set(builder, rows);
        for (const row of rows) this.initializeRow(row);
        builder.hidden = true;
        const form = builder.querySelector('form');
        form.addEventListener('change', (event) => {
          const row = event.target.closest('[data-look-item]');
          if (!row) return;
          row.lookTouched = true;
          if (event.target.matches('[data-look-variant]')) this.rememberSize(row);
          this.update(builder);
        }, { signal: this.abort.signal });
        form.addEventListener('submit', (event) => { event.preventDefault(); this.add(builder); }, { signal: this.abort.signal });
        this.update(builder);
      }
      const cards = this.entry.querySelector('.babauba-looks__cards');
      [...cards.children].sort((a,b) => Number(a.dataset.lookOrder) - Number(b.dataset.lookOrder)).forEach(card => cards.append(card));
      this.entry.addEventListener('click', (event) => {
        const link = event.target.closest('[data-look-open]');
        if (!link) return;
        event.preventDefault();
        this.open(link.dataset.lookOpen, true);
      }, { signal: this.abort.signal });
      if (this.dataset.context === 'product') {
        const slot = [...document.querySelectorAll('[data-look-entry-slot]')].find(el => el.dataset.lookEntrySlot === this.dataset.section);
        slot?.append(this.entry);
        this.productInfo = this.closest('product-info');
        document.addEventListener('variant:changed', (event) => {
          const variant = event.detail?.variant;
          if ([...this.rows.values()].flat().some(row => row.dataset.productId === this.dataset.productId && row.lookData.variants.some(v => v.id === variant?.id))) this.pdpVariant = variant.id;
        }, { signal: this.abort.signal });
      } else if (this.builders.length === 1) this.open(this.builders[0].dataset.lookBuilder, false);
      this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (payload) => this.cartChanged(payload));
      document.addEventListener('cart:refresh', () => this.refreshState(), { signal: this.abort.signal });
    }
    disconnectedCallback() {
      this.abort?.abort(); this.stateRequest?.abort(); this.unsubscribe?.();
      if (this.entry && !this.contains(this.entry)) this.entry.remove();
      this.initialized = false;
    }
    initializeRow(row) {
      row.lookData = JSON.parse(row.querySelector('[data-look-product]').textContent);
      const select = row.querySelector('[data-look-variant]');
      if (!select) return;
      const preference = window.BabaubaSizePreference;
      const sizeIndex = row.lookData.options.findIndex(name => ['größe','grösse','groesse','size'].includes(name.toLowerCase().trim()));
      if (sizeIndex < 0 || !preference) return;
      // Reuse the existing preference contract; never map a double size to a single size.
      const variants = row.lookData.variants;
      const sizes = variants.map(v => preference.normalize(v.options[sizeIndex]));
      const kind = sizes[0]?.kind;
      if (!kind || sizes.some(s => s?.kind !== kind) || new Set(sizes.map(s => s.value)).size !== sizes.length) return;
      row.sizePicker = document.createElement('div');
      const field = document.createElement('div'); field.dataset.optionName = row.lookData.options[sizeIndex]; field.dataset.optionPosition = String(sizeIndex + 1);
      const sizeSelect = document.createElement('select');
      variants.forEach((variant, index) => {
        const option = document.createElement('option'); option.value = sizes[index].value; option.dataset.variantAvailable = String(variant.available); option.dataset.variantId = String(variant.id); sizeSelect.append(option);
      });
      field.append(sizeSelect); row.sizePicker.append(field);
      const preferred = preference.preferredControl(row.sizePicker);
      if (preferred) select.value = preferred.dataset.variantId;
    }
    rememberSize(row) {
      const select = row.querySelector('[data-look-variant]');
      const control = row.sizePicker && [...row.sizePicker.querySelectorAll('option')].find(option => option.dataset.variantId === select.value);
      if (control) window.BabaubaSizePreference.remember(row.sizePicker, control);
    }
    open(handle, focus) {
      const active = this.builders.find(builder => builder.dataset.lookBuilder === handle);
      if (!active) return;
      const explicit = this.productInfo && window.BabaubaSizePreference?.hasExplicitSelection(this.productInfo);
      const variantId = this.pdpVariant || (explicit ? this.productInfo.currentVariant?.id || new URL(window.location.href).searchParams.get('variant') : null);
      for (const row of this.rows.get(active)) {
        if (!row.lookOpened && !row.lookTouched) {
          const select = row.querySelector('[data-look-variant]');
          if (select && !select.hidden) select.value = '';
          this.initializeRow(row);
        }
        row.lookOpened = true;
        if (row.lookTouched || row.dataset.productId !== this.dataset.productId || !variantId) continue;
        const variant = row.lookData.variants.find(v => String(v.id) === String(variantId) && v.available);
        if (variant) row.querySelector('[data-look-variant]').value = String(variant.id);
      }
      for (const builder of this.builders) builder.hidden = builder !== active;
      this.entry.querySelectorAll('[data-look-open]').forEach(link => link.setAttribute('aria-expanded', String(link.dataset.lookOpen === handle)));
      this.update(active);
      if (focus) {
        active.querySelector('h3').focus({ preventScroll: true });
        active.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }
    }
    selection(builder) {
      return this.rows.get(builder).filter(row => row.querySelector('[data-look-selected]').checked).map(row => ({ row, variant: row.lookData.variants.find(v => String(v.id) === row.querySelector('[data-look-variant]')?.value && v.available) }));
    }
    update(builder) {
      const chosen = this.selection(builder);
      let total = 0, eligible = 0;
      for (const row of this.rows.get(builder)) {
        const variant = row.lookData.variants.find(v => String(v.id) === row.querySelector('[data-look-variant]')?.value);
        if (variant) row.querySelector('[data-look-price]').textContent = this.money.format(variant.price / 100);
      }
      for (const {row, variant} of chosen) {
        if (row.dataset.bonusEligible === 'true') eligible++;
        if (variant) total += variant.price;
      }
      const complete = chosen.length > 0 && chosen.every(({variant}) => variant);
      const count = eligible + this.eligibleCount;
      const rate = count >= 4 ? 15 : count === 3 ? 10 : count === 2 ? 5 : 0;
      // Shopify rounds line discounts; each selected product contributes one piece.
      const saving = chosen.reduce((sum, {row,variant}) => sum + (variant && row.dataset.bonusEligible === 'true' ? Math.round(variant.price * rate / 100) : 0), 0);
      builder.querySelector('[data-look-summary]').textContent = `${chosen.length} ${chosen.length === 1 ? 'Teil' : 'Teile'} ausgewählt${complete || !chosen.length ? '' : ' · bitte Größen wählen'}`;
      builder.querySelector('[data-look-total]').textContent = complete ? this.money.format(total / 100) : '–';
      const bonusPanel = builder.querySelector('[data-look-bonus-panel]');
      const knownCount = Number.isInteger(this.eligibleCount);
      bonusPanel.hidden = eligible === 0;
      builder.querySelector('[data-look-bonus-context]').textContent = knownCount
        ? `Mit deiner Auswahl: ${count} Bonusartikel${this.eligibleCount ? `, davon ${this.eligibleCount} bereits im Warenkorb` : ''}`
        : 'Der Bonus wird im Warenkorb berechnet.';
      builder.querySelectorAll('[data-look-bonus-tier]').forEach(step => {
        const tier = Number(step.dataset.lookBonusTier);
        const reached = knownCount && count >= tier;
        step.classList.toggle('is-reached', reached);
        if (knownCount && Math.min(count, 4) === tier) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
      const bonus = builder.querySelector('[data-look-bonus]');
      const priceKnown = complete && (knownCount || eligible === 0);
      builder.querySelector('[data-look-payable]').textContent = priceKnown ? this.money.format((total - saving) / 100) : '–';
      builder.querySelector('[data-look-total]').classList.toggle('is-discounted', priceKnown && saving > 0);
      builder.querySelector('[data-look-saving-row]').hidden = !priceKnown;
      bonus.replaceChildren();
      if (priceKnown) {
        const amount = document.createElement('strong');
        amount.textContent = this.money.format(saving / 100);
        const caption = document.createElement('span');
        caption.textContent = eligible > 0 ? ` (${rate} % auf berechtigte Teile)` : '';
        bonus.append(amount, caption);
      }
      builder.querySelector('button[type="submit"]').disabled = this.busy || !complete;
    }
    applyState(html) {
      const state = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bonus-state]');
      const count = Number(state?.dataset.count);
      if (!state || !Number.isInteger(count) || count < 0) { this.eligibleCount = NaN; } else this.eligibleCount = count;
      this.builders.forEach(builder => this.update(builder));
    }
    async refreshState() {
      this.stateRequest?.abort();
      const controller = new AbortController(); this.stateRequest = controller;
      try {
        const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin); url.searchParams.set('section_id', stateSection);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('state');
        const html = await response.text(); if (!controller.signal.aborted) this.applyState(html);
      } catch (error) { if (error.name !== 'AbortError') { this.eligibleCount = NaN; this.builders.forEach(builder => this.update(builder)); } }
    }
    cartChanged(payload) {
      const html = payload?.cart?.sections?.[stateSection];
      this.stateRequest?.abort();
      if (html) this.applyState(html); else this.refreshState();
    }
    async add(builder) {
      if (this.busy) return;
      const selected = this.selection(builder);
      if (!selected.length || selected.some(item => !item.variant)) return;
      this.busy = true; this.builders.forEach(item => this.update(item));
      const error = builder.querySelector('[data-look-error]'); error.hidden = true;
      const button = builder.querySelector('button[type="submit"]'); button.textContent = 'Wird hinzugefügt …';
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
      if (!sections.includes(stateSection) && sections.length < 5) sections.push(stateSection);
      let result, added = false;
      try {
        const items = selected.map(({row,variant}) => {
          const item = { id: variant.id, quantity: 1 };
          if (row.dataset.preorderDate) item.properties = { [row.dataset.preorderName]: row.dataset.preorderValue, _preorder: 'true', _preorder_shipping_date: row.dataset.preorderDate };
          return item;
        });
        const response = await fetch(FoxTheme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: window.location.pathname }) });
        result = await response.json();
        if (!response.ok || result.status || result.errors) throw new Error(typeof result.description === 'string' ? result.description : 'Die Auswahl konnte nicht vollständig hinzugefügt werden. Bitte prüfe deinen Warenkorb.');
        added = true;
      } catch (failure) {
        error.textContent = failure instanceof TypeError || failure instanceof SyntaxError ? 'Die Verbindung wurde unterbrochen. Bitte prüfe deinen Warenkorb, bevor du erneut hinzufügst.' : failure.message;
        error.hidden = false;
      }
      // Always reconcile: a timed-out response can still have changed the real cart.
      try {
        const response = await fetch(`${FoxTheme.routes.cart_url.replace(/\.js$/, '')}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (added) cart.sections = result.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        if (!added) document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
        if (added) {
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: result } }));
          const drawer = document.querySelector('cart-drawer');
          if (drawer && FoxTheme.settings.cartType === 'drawer') drawer.show(button);
          else window.location.assign(FoxTheme.routes.cart_url.replace(/\.js(?:\?.*)?$/, ''));
        }
      } catch (_) {
        this.eligibleCount = NaN;
        error.textContent = added ? 'Deine Auswahl wurde hinzugefügt. Der Warenkorb konnte nicht aktualisiert werden. Bitte öffne ihn, bevor du erneut hinzufügst.' : 'Der Warenkorb konnte nicht geprüft werden. Bitte öffne ihn, bevor du erneut hinzufügst.';
        error.hidden = false;
      } finally {
        this.busy = false; button.textContent = 'Auswahl in den Warenkorb'; this.builders.forEach(item => this.update(item));
      }
    }
  }
  customElements.define('babauba-looks', BabaubaLooks);
})();
